import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import useTableDensity from '@/hooks/useTableDensity';
import Blade from '@/components/ui/Blade/Blade';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import downloadIcon from '@/assets/icons/download.svg';
import eyeIcon from '@/assets/icons/eye.svg';
import { PendingOfflineDonation, OfflineDonationStatus } from './types';
import { findDestinationBank } from '@/pages/Donation/PublicDonation/destinationBankAccounts';
import { downloadProof } from '@/pages/Management/OfflineDonations/downloadFile';
import mgStyles from '@/pages/Management/OfflineDonations/OfflineDonationsManagement.module.css';
import filtersStyles from '@/components/filters/FiltersArea.module.scss';

const DENSITY_STORAGE_KEY = 'table.density.pendingDonations';

const STATUSES: OfflineDonationStatus[] = ['Pending', 'InProgress', 'Approved', 'Rejected'];

const STATUS_COLORS: Record<OfflineDonationStatus, string> = {
  Pending: '#d97706',
  InProgress: '#2563eb',
  FutureCheck: '#7c3aed',
  Approved: '#059669',
  Rejected: '#dc2626',
};

const statusClass = (s: string) => {
  switch (s) {
    case 'Pending': return mgStyles.statusPending;
    case 'InProgress': return mgStyles.statusInProgress;
    case 'FutureCheck': return mgStyles.statusFutureCheck;
    case 'Approved': return mgStyles.statusApproved;
    case 'Rejected': return mgStyles.statusRejected;
    default: return '';
  }
};

interface Props {
  fundId: number;
}

const PendingDonationsList = ({ fundId }: Props) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const { data: donations = [], isLoading } = useGetAuthApi<PendingOfflineDonation[]>(`donations/pending/${fundId}`);

  const [selected, setSelected] = useState<PendingOfflineDonation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [statusFilter, setStatusFilter] = useState<OfflineDonationStatus | null>(null);

  const filteredDonations = useMemo(() => {
    if (!statusFilter) return donations;
    return donations.filter((d) => d.status === statusFilter);
  }, [donations, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of donations) counts[d.status] = (counts[d.status] || 0) + 1;
    return counts;
  }, [donations]);

  const statusLabel = (s: string) => t(`donations.management.status.${s}`);
  const typeLabel = (type: string) => t(`donations.methods.${type === 'BankTransfer' ? 'ManualBankTransfer' : 'Check'}`);
  const currencySymbol = (c: string) => c === 'USD' ? '$' : '₪';

  const exportColumns: ColumnDef<PendingOfflineDonation>[] = [
    { id: 'createdAt', header: t('donations.management.col.date'), accessorKey: 'createdAt' },
    { id: 'donorFullName', header: t('donations.management.col.donor'), accessorKey: 'donorFullName' },
    { id: 'donorPhone', header: t('phoneNumber'), accessorKey: 'donorPhone' },
    { id: 'donorEmail', header: t('email'), accessorKey: 'donorEmail' },
    { id: 'donorIsraeliId', header: t('donations.israeliId'), accessorKey: 'donorIsraeliId' },
    { id: 'type', header: t('donations.management.col.type'), accessorFn: (row) => typeLabel(row.type) },
    { id: 'amount', header: t('donations.management.col.amount'), accessorFn: (row) => `${currencySymbol(row.currency)}${row.amount.toLocaleString()}` },
    { id: 'bankNumber', header: t('donations.offline.bankNumber'), accessorKey: 'bankNumber' },
    { id: 'branchNumber', header: t('donations.offline.branchNumber'), accessorKey: 'branchNumber' },
    { id: 'accountNumber', header: t('donations.offline.accountNumber'), accessorKey: 'accountNumber' },
    { id: 'checkNumber', header: t('donations.offline.checkNumber'), accessorKey: 'checkNumber' },
    { id: 'checkSubType', header: t('donations.offline.checkSubType'), accessorFn: (row) => row.checkSubType ? t(`donations.offline.${row.checkSubType === 'Deposited' ? 'checkDeposited' : 'checkForDeposit'}`) : '' },
    { id: 'destinationBankAccount', header: t('donations.offline.destinationBankAccount'), accessorFn: (row) => findDestinationBank(row.destBankNumber, row.destBranchNumber, row.destAccountNumber)?.label ?? (row.destBankNumber ? `${row.destBankNumber}/${row.destBranchNumber}/${row.destAccountNumber}` : '') },
    { id: 'maturityDate', header: t('donations.offline.maturityDate'), accessorKey: 'maturityDate' },
    { id: 'status', header: t('donations.management.col.status'), accessorFn: (row) => statusLabel(row.status) },
    { id: 'rejectionReason', header: t('donations.management.rejectionReason'), accessorKey: 'rejectionReason' },
  ];

  const columns: ColumnDef<PendingOfflineDonation>[] = [
    {
      id: 'createdAt',
      header: Col(t('donations.management.col.date')),
      accessorKey: 'createdAt',
      cell: Cell(cellType.Date),
      enableSorting: true,
    },
    {
      id: 'donorFullName',
      header: Col(t('donations.management.col.donor')),
      accessorKey: 'donorFullName',
      cell: ({ row }) => (
        <div className={mgStyles.donorCell}>
          <span>{row.original.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</span>
          {(row.original.donorPhone || row.original.donorEmail) && (
            <span className={mgStyles.donorCellSub}>
              {[row.original.donorPhone, row.original.donorEmail].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ),
      enableSorting: true,
    },
    {
      id: 'type',
      header: Col(t('donations.management.col.type')),
      accessorFn: (row) => typeLabel(row.type),
      enableSorting: true,
    },
    {
      id: 'amount',
      header: Col(t('donations.management.col.amount')),
      accessorFn: (row) => `${currencySymbol(row.currency)}${row.amount.toLocaleString()}`,
      enableSorting: true,
    },
    {
      id: 'status',
      header: Col(t('donations.management.col.status')),
      accessorKey: 'status',
      cell: ({ row }) => (
        <span className={`${mgStyles.statusBadge} ${statusClass(row.original.status)}`}>
          {statusLabel(row.original.status)}
        </span>
      ),
      enableSorting: true,
    },
  ];

  const bladeHeaderTop = selected ? (
    <span className="body-lr-regular">
      {typeLabel(selected.type)}
      {' - '}
      <span>{selected.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</span>
    </span>
  ) : null;

  const bladeHeaderBottom = selected ? (
    <>
      <span className="body-sm-medium">
        {currencySymbol(selected.currency)}{selected.amount.toLocaleString()}
      </span>
      <span className="body-sm-regular">
        {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(selected.createdAt))}
      </span>
    </>
  ) : null;

  return (
    <div>
      <DataTable
        title={t('donations.admin.pendingTitle')}
        data={filteredDonations}
        isLoading={isLoading}
        columns={columns}
        exportColumns={exportColumns}
        exportFilename={t('donations.admin.pendingTitle')}
        handleRowClick={(row: PendingOfflineDonation) => setSelected(row)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="pendingDonations"
        searchPlaceholder="donations.management.search"
        customGlobalSearch={() => true}
        filters={
          <div className={filtersStyles.filtersArea}>
            <div className={filtersStyles.filtersContainer}>
              <StatusFilter
                title="donations.management.status.all"
                isSelected={statusFilter === null}
                onClick={() => setStatusFilter(null)}
                count={donations.length}
              />
              {STATUSES.map((s) => (
                <StatusFilter
                  key={s}
                  title={`donations.management.status.${s}`}
                  isSelected={statusFilter === s}
                  onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                  iconColor={STATUS_COLORS[s]}
                  count={statusCounts[s] || 0}
                />
              ))}
              <span className={filtersStyles.statusDivider} aria-hidden />
              <StatusFilter
                title="donations.management.status.FutureCheck"
                isSelected={statusFilter === 'FutureCheck'}
                onClick={() => setStatusFilter(statusFilter === 'FutureCheck' ? null : 'FutureCheck')}
                iconColor={STATUS_COLORS.FutureCheck}
                count={statusCounts.FutureCheck || 0}
              />
            </div>
          </div>
        }
      />

      {selected && (
        <Blade
          handleXclick={() => setSelected(null)}
          width="456px"
          headerTop={bladeHeaderTop}
          headerBottom={bladeHeaderBottom}
        >
          <div className={`${mgStyles.bladeContent} custom-scrollbar`}>
            <section className={mgStyles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('donations.management.col.status')}</h3>
              <div className="body-md-regular">
                <span className={`${mgStyles.statusBadge} ${statusClass(selected.status)}`}>
                  {statusLabel(selected.status)}
                </span>
              </div>
            </section>

            <div className={mgStyles.line} />

            <section className={mgStyles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('donations.donorDetails')}</h3>
              <div className="body-md-regular text-(--font-body-1)">
                <p>{selected.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</p>
                {selected.donorPhone && <p>{selected.donorPhone}</p>}
                {selected.donorEmail && <p>{selected.donorEmail}</p>}
                {selected.donorIsraeliId && <p>{t('donations.israeliId')}: {selected.donorIsraeliId}</p>}
              </div>
            </section>

            <div className={mgStyles.line} />

            <section className={mgStyles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">
                {selected.type === 'Check' ? t('donations.offline.checkDetails') : t('donations.offline.bankDetails')}
              </h3>
              <div className="body-md-regular text-(--font-body-2)">
                {selected.type === 'Check' && selected.checkSubType && (
                  <p>{t('donations.offline.checkSubType')}: {t(`donations.offline.${selected.checkSubType === 'Deposited' ? 'checkDeposited' : 'checkForDeposit'}`)}</p>
                )}
                {selected.bankNumber && <p>{t('donations.offline.bankNumber')}: {selected.bankNumber}</p>}
                {selected.branchNumber && <p>{t('donations.offline.branchNumber')}: {selected.branchNumber}</p>}
                {selected.accountNumber && <p>{t('donations.offline.accountNumber')}: {selected.accountNumber}</p>}
                {selected.checkNumber && <p>{t('donations.offline.checkNumber')}: {selected.checkNumber}</p>}
                {selected.maturityDate && <p>{t('donations.offline.maturityDate')}: {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(selected.maturityDate))}</p>}
              </div>
            </section>

            {(selected.type === 'BankTransfer' || (selected.type === 'Check' && selected.checkSubType === 'Deposited')) && (
              <>
                <div className={mgStyles.line} />
                <section className={mgStyles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.offline.destinationBankAccount')}</h3>
                  <p className="body-md-regular text-(--font-body-2)">
                    {selected.destBankNumber
                      ? (findDestinationBank(selected.destBankNumber, selected.destBranchNumber, selected.destAccountNumber)?.label ?? `${selected.destBankNumber}/${selected.destBranchNumber}/${selected.destAccountNumber}`)
                      : t('donations.offline.notSet')}
                  </p>
                </section>
              </>
            )}

            {(selected.proofUrl || selected.secondProofUrl) && (
              <>
                <div className={mgStyles.line} />
                <section className={mgStyles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.offline.attachments')}</h3>
                  <div className={mgStyles.proofLinks}>
                    {selected.proofUrl && (
                      <div className={mgStyles.proofRow}>
                        <a href={selected.proofUrl} target="_blank" rel="noreferrer" className={mgStyles.proofLink}>
                          📎 {selected.type === 'Check' ? t('donations.checkFront') : t('donations.proofFile')}
                        </a>
                        <div className={mgStyles.proofActions}>
                          <a href={selected.proofUrl} target="_blank" rel="noreferrer" className={mgStyles.downloadBtn} title={t('view')}>
                            <img src={eyeIcon} alt="" />
                          </a>
                          <button type="button" onClick={() => downloadProof(selected.id, 'proof', currentUser?.token ?? '')} className={mgStyles.downloadBtn} title={t('download')}>
                            <img src={downloadIcon} alt="" />
                          </button>
                        </div>
                      </div>
                    )}
                    {selected.secondProofUrl && (
                      <div className={mgStyles.proofRow}>
                        <a href={selected.secondProofUrl} target="_blank" rel="noreferrer" className={mgStyles.proofLink}>
                          📎 {t('donations.checkBack')}
                        </a>
                        <div className={mgStyles.proofActions}>
                          <a href={selected.secondProofUrl} target="_blank" rel="noreferrer" className={mgStyles.downloadBtn} title={t('view')}>
                            <img src={eyeIcon} alt="" />
                          </a>
                          <button type="button" onClick={() => downloadProof(selected.id, 'second', currentUser?.token ?? '')} className={mgStyles.downloadBtn} title={t('download')}>
                            <img src={downloadIcon} alt="" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            {selected.rejectionReason && (
              <>
                <div className={mgStyles.line} />
                <section className={mgStyles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.management.rejectionReason')}</h3>
                  <p className="body-md-regular" style={{ color: 'var(--status-rejected-text, #991b1b)' }}>{selected.rejectionReason}</p>
                </section>
              </>
            )}
          </div>
        </Blade>
      )}
    </div>
  );
};

export default PendingDonationsList;
